import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useLanguage } from "../../LanguageContext";
import {
	applyLetterVariant,
	pickVariant,
	SETTLED,
	variantKey,
} from "../../lib/letterSets";
import { afterLoadIdle, clamp, dockDistance, easeOut } from "../../lib/motion";

const GLITCH_EVERY_MS = 3000;
const GLITCH_BURST_MS = 1100;
const GLITCH_TICK_MS = 90;
const FIRST_GLITCH_MS = 500;
const KEEP_SETTLE_FROM = 0.78;

const GLITCH_COLORS = [
	"#ea4e2c",
	"#e23722",
	"#00e5ff",
	"#ff2bd6",
	"#39ff14",
	"#ffe600",
	"#4d7cff",
	"#ffffff",
	"#7cf5ff",
	"#ff6b4a",
];

function pickGlitchColor(exclude?: string) {
	const pool = exclude
		? GLITCH_COLORS.filter((color) => color !== exclude)
		: GLITCH_COLORS;
	return pool[Math.floor(Math.random() * pool.length)];
}

function extraDropOrder(firstLen: number, total: number) {
	const order: number[] = [];
	for (let i = total - 1; i > firstLen; i -= 1) order.push(i);
	for (let i = firstLen - 1; i >= 1; i -= 1) order.push(i);
	return order;
}

function erasedCount(progress: number, total: number) {
	const eraseStart = 0.06;
	const eraseEnd = 0.5;
	return Math.min(
		total,
		Math.floor(
			clamp((progress - eraseStart) / (eraseEnd - eraseStart)) * total + 1e-6,
		),
	);
}

export default function NameIntro() {
	const { text } = useLanguage();
	const location = useLocation();
	const layerRef = useRef<HTMLDivElement>(null);
	const textRef = useRef<HTMLAnchorElement>(null);
	const lastLineRef = useRef<HTMLSpanElement>(null);
	const slashRef = useRef<HTMLSpanElement>(null);
	const charWraps = useRef<Array<HTMLSpanElement | null>>([]);
	const charsRef = useRef<Array<HTMLSpanElement | null>>([]);
	const keysRef = useRef<string[]>([]);
	const dockedRef = useRef(false);
	const glitchActive = useRef<number[]>([]);
	const glitchInterval = useRef(0);
	const glitchBurst = useRef(0);
	const glitchTick = useRef(0);
	const glitchFirst = useRef(0);
	const idleGlitch = useRef(false);
	const scrollKeepGlitch = useRef(false);
	const [live, setLive] = useState(
		() => typeof window !== "undefined" && window.scrollY > 0,
	);

	const first = text.site.displayName[0];
	const last = text.site.displayName[1];
	const target = `${first}${last}`;
	const keepO = first.length;
	const mark = text.site.wordmark;

	useLayoutEffect(() => {
		document.getElementById("lcp-shell")?.setAttribute("hidden", "");
	}, []);

	useEffect(() => {
		if (location.pathname !== "/" || live) return;

		const enable = () => setLive(true);
		const stopIdle = afterLoadIdle(enable, 1800);
		window.addEventListener("scroll", enable, { once: true, passive: true });
		window.addEventListener("pointerdown", enable, { once: true });

		return () => {
			stopIdle();
			window.removeEventListener("scroll", enable);
			window.removeEventListener("pointerdown", enable);
		};
	}, [location.pathname, live]);

	useLayoutEffect(() => {
		if (location.pathname !== "/" || !live) return;

		const reduced = window.matchMedia(
			"(prefers-reduced-motion: reduce)",
		).matches;
		keysRef.current = target
			.split("")
			.map((letter) => `${letter}:900:normal:normal:normal`);
		let frame = 0;

		const dropOrder = extraDropOrder(keepO, target.length);
		const keepIndexes = [0, keepO];

		const measureStart = () => {
			const text = textRef.current;
			const slash = slashRef.current;
			const mWrap = charWraps.current[0];
			if (!text || !slash || !mWrap) return;
			const previous = text.style.transform;
			const linePrev = lastLineRef.current?.style.transform || "";
			const slashPrev = slash.style.transform;
			text.style.transform = "none";
			if (lastLineRef.current) lastLineRef.current.style.transform = "none";
			slash.style.transform = "none";

			const slashWidthPrev = slash.style.width;
			const slashOverflowPrev = slash.style.overflow;
			slash.style.width = "auto";
			slash.style.overflow = "visible";
			slash.style.opacity = "0";
			slash.dataset.naturalWidth = String(slash.getBoundingClientRect().width);
			slash.style.width = slashWidthPrev;
			slash.style.overflow = slashOverflowPrev;

			const textRect = text.getBoundingClientRect();
			text.dataset.startLeft = String(textRect.left);
			text.dataset.startTop = String(textRect.top);

			const firstLine = text.querySelector(".name-line");
			text.dataset.lineHeight = String(
				firstLine?.getBoundingClientRect().height || textRect.height / 2,
			);

			charWraps.current.forEach((wrap) => {
				if (!wrap) return;
				const hide = wrap.style.width;
				wrap.style.width = "auto";
				wrap.style.overflow = "visible";
				wrap.dataset.naturalWidth = String(wrap.getBoundingClientRect().width);
				wrap.style.width = hide;
			});

			const mRect = mWrap.getBoundingClientRect();
			mWrap.dataset.startLeft = String(mRect.left);
			mWrap.dataset.startTop = String(mRect.top);
			mWrap.dataset.naturalHeight = String(mRect.height);

			text.style.transform = previous;
			if (lastLineRef.current) lastLineRef.current.style.transform = linePrev;
			slash.style.transform = slashPrev;
		};

		const ghostParts = (wrap: HTMLElement | null) => ({
			ghostA: wrap?.querySelector<HTMLElement>(".name-char-ghost-a") || null,
			ghostB: wrap?.querySelector<HTMLElement>(".name-char-ghost-b") || null,
		});

		const syncGlyph = (index: number, char: string) => {
			const wrap = charWraps.current[index];
			const live = charsRef.current[index];
			const { ghostA, ghostB } = ghostParts(wrap);
			if (live) live.textContent = char;
			if (ghostA) ghostA.textContent = char;
			if (ghostB) ghostB.textContent = char;
		};

		const applyVariantAt = (
			index: number,
			variant: Parameters<typeof applyLetterVariant>[1],
		) => {
			const live = charsRef.current[index];
			if (!live) return;
			applyLetterVariant(live, variant);
			syncGlyph(index, variant.char);
			const { ghostA, ghostB } = ghostParts(charWraps.current[index]);
			for (const ghost of [ghostA, ghostB]) {
				if (!ghost) continue;
				ghost.style.fontWeight = String(variant.weight);
				ghost.style.fontStyle = variant.style;
				ghost.style.fontVariantCaps = variant.caps;
				ghost.style.fontStretch = variant.stretch;
			}
		};

		const clearLetterGlitch = (index: number) => {
			const wrap = charWraps.current[index];
			if (!wrap) return;
			wrap.classList.remove("is-glitch");
			wrap.style.removeProperty("--glitch-a");
			wrap.style.removeProperty("--glitch-b");
			const { ghostA, ghostB } = ghostParts(wrap);
			if (ghostA) ghostA.style.cssText = "";
			if (ghostB) ghostB.style.cssText = "";
		};

		const clearGlitchBurst = () => {
			window.clearTimeout(glitchBurst.current);
			window.clearInterval(glitchTick.current);
			glitchBurst.current = 0;
			glitchTick.current = 0;
			glitchActive.current.forEach(clearLetterGlitch);
			glitchActive.current = [];
		};

		const stopIdleGlitch = () => {
			window.clearTimeout(glitchFirst.current);
			window.clearInterval(glitchInterval.current);
			glitchFirst.current = 0;
			glitchInterval.current = 0;
			idleGlitch.current = false;
			if (!scrollKeepGlitch.current) clearGlitchBurst();
		};

		const settleLetter = (index: number, char: string) => {
			applyVariantAt(index, { char, ...SETTLED });
			keysRef.current[index] = `${char}:900:normal:normal:normal`;
			clearLetterGlitch(index);
		};

		const settleIdle = () => {
			for (let i = 0; i < target.length; i += 1) {
				if (!charsRef.current[i]) continue;
				settleLetter(i, target[i]);
			}
		};

		const scrambleGlitchLetters = () => {
			glitchActive.current.forEach((index) => {
				const source = target[index];
				const variant = pickVariant(source, keysRef.current[index] || "");
				applyVariantAt(index, variant);
				keysRef.current[index] = variantKey(variant);
			});
		};

		const armGlitch = (indexes: number[]) => {
			indexes.forEach((index) => {
				const wrap = charWraps.current[index];
				if (!wrap) return;
				const colorA = pickGlitchColor();
				const colorB = pickGlitchColor(colorA);
				wrap.style.setProperty("--glitch-a", colorA);
				wrap.style.setProperty("--glitch-b", colorB);
				wrap.classList.add("is-glitch");
			});
			glitchActive.current = indexes;
			scrambleGlitchLetters();
			window.clearInterval(glitchTick.current);
			glitchTick.current = window.setInterval(
				scrambleGlitchLetters,
				GLITCH_TICK_MS,
			);
		};

		const startIdleBurst = () => {
			if (scrollKeepGlitch.current) return;
			clearGlitchBurst();
			const unlocked: number[] = [];
			for (let i = 0; i < target.length; i += 1) {
				if (charsRef.current[i] && target[i]?.trim()) unlocked.push(i);
			}
			if (!unlocked.length) return;

			const count = Math.max(
				1,
				Math.floor(Math.random() * Math.min(unlocked.length, 4)) + 1,
			);
			const pool = [...unlocked];
			for (let i = pool.length - 1; i > 0; i -= 1) {
				const j = Math.floor(Math.random() * (i + 1));
				[pool[i], pool[j]] = [pool[j], pool[i]];
			}
			armGlitch(pool.slice(0, Math.min(count, pool.length)));
			glitchBurst.current = window.setTimeout(
				clearGlitchBurst,
				GLITCH_BURST_MS,
			);
		};

		const startIdleGlitch = () => {
			if (reduced || idleGlitch.current) return;
			idleGlitch.current = true;
			settleIdle();
			glitchFirst.current = window.setTimeout(() => {
				startIdleBurst();
				glitchInterval.current = window.setInterval(
					startIdleBurst,
					GLITCH_EVERY_MS,
				);
			}, FIRST_GLITCH_MS);
		};

		const startKeepScrollGlitch = () => {
			if (reduced || scrollKeepGlitch.current) return;
			scrollKeepGlitch.current = true;
			window.clearTimeout(glitchBurst.current);
			glitchBurst.current = 0;
			armGlitch(
				keepIndexes.filter((index) => Boolean(charsRef.current[index])),
			);
		};

		const stopKeepScrollGlitch = () => {
			if (!scrollKeepGlitch.current) return;
			scrollKeepGlitch.current = false;
			clearGlitchBurst();
		};

		const settleKeepLower = () => {
			stopKeepScrollGlitch();
			settleLetter(0, mark[0]);
			settleLetter(keepO, mark[2]);
		};

		const applyLetters = (progress: number, reducedMotion: boolean) => {
			if (reducedMotion) {
				stopIdleGlitch();
				stopKeepScrollGlitch();
				for (let i = 0; i < target.length; i += 1) {
					if (!charsRef.current[i]) continue;
					const lower =
						i === 0 ? mark[0] : i === keepO ? mark[2] : target[i].toLowerCase();
					settleLetter(i, lower);
				}
				return;
			}

			if (progress <= 0) {
				stopKeepScrollGlitch();
				startIdleGlitch();
				return;
			}

			if (idleGlitch.current) stopIdleGlitch();

			if (progress < KEEP_SETTLE_FROM) {
				startKeepScrollGlitch();
				return;
			}

			settleKeepLower();
		};

		const setLetterVisible = (wrap: HTMLElement | null, visible: boolean) => {
			if (!wrap) return;
			if (visible) {
				wrap.style.display = "";
				wrap.style.width = "";
				wrap.style.overflow = "";
				wrap.style.padding = "";
				wrap.style.visibility = "";
				return;
			}
			wrap.style.display = "none";
		};

		let dockMeasured = false;

		const applyPose = (progress: number) => {
			const layer = layerRef.current;
			const text = textRef.current;
			const slash = slashRef.current;
			const lastLine = lastLineRef.current;
			const mWrap = charWraps.current[0];
			const oWrap = charWraps.current[keepO];
			const mSlot = document.querySelector<HTMLElement>("[data-wordmark-m]");
			const slashSlot = document.querySelector<HTMLElement>(
				"[data-wordmark-slash]",
			);
			const oSlot = document.querySelector<HTMLElement>("[data-wordmark-o]");
			if (
				!layer ||
				!text ||
				!slash ||
				!lastLine ||
				!mWrap ||
				!oWrap ||
				!mSlot ||
				!slashSlot ||
				!oSlot
			) {
				return;
			}

			const t = easeOut(progress);

			if (t <= 0) {
				dockMeasured = false;
				dropOrder.forEach((index) =>
					setLetterVisible(charWraps.current[index], true),
				);
				setLetterVisible(mWrap, true);
				setLetterVisible(oWrap, true);
				mWrap.style.padding = "";
				oWrap.style.padding = "";
				slash.style.width = "";
				slash.style.opacity = "";
				slash.style.overflow = "";
				slash.style.transform = "";
				lastLine.style.transform = "";
				text.style.transform = "";
				text.style.transformOrigin = "";
			} else {
				mWrap.style.padding = "0";
				oWrap.style.padding = "0";
				if (!dockMeasured) {
					measureStart();
					dockMeasured = true;
				}

				const erased = erasedCount(progress, dropOrder.length);
				dropOrder.forEach((index, order) => {
					setLetterVisible(charWraps.current[index], order >= erased);
				});
				setLetterVisible(mWrap, true);
				setLetterVisible(oWrap, true);

				const slashOn = progress >= 0.54;
				const slashWidth = slashOn
					? Number(slash.dataset.naturalWidth || 0)
					: 0;
				slash.style.width = slashOn ? "auto" : "";
				slash.style.opacity = slashOn ? "1" : "";
				slash.style.overflow = "";

				const merge = easeOut(clamp((t - 0.62) / 0.28));
				const mWidth = Number(mWrap.dataset.naturalWidth || 0);
				const lineHeight = Number(text.dataset.lineHeight || 0);
				const natX = (mWidth + slashWidth) * merge;
				const natY = -lineHeight * merge;
				lastLine.style.transform = `translate3d(${natX}px, ${natY}px, 0)`;
				slash.style.transform = "none";

				const originX =
					Number(mWrap.dataset.startLeft || 0) -
					Number(text.dataset.startLeft || 0);
				const originY =
					Number(mWrap.dataset.startTop || 0) -
					Number(text.dataset.startTop || 0);
				text.style.transformOrigin = `${originX}px ${originY}px`;

				const dest = mSlot.getBoundingClientRect();
				const startLeft = Number(mWrap.dataset.startLeft || 0);
				const startTop = Number(mWrap.dataset.startTop || 0);
				const startHeight = Number(mWrap.dataset.naturalHeight || 1);
				const targetScale = dest.height / startHeight;
				let tx = (dest.left - startLeft) * t;
				let ty = (dest.top - startTop) * t;
				let scale = 1 + (targetScale - 1) * t;
				text.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;

				const settle = clamp((t - 0.62) / 0.38);
				if (settle > 0) {
					const mNow = mWrap.getBoundingClientRect();
					const mWant = mSlot.getBoundingClientRect();
					tx += (mWant.left - mNow.left) * settle;
					ty += (mWant.top - mNow.top) * settle;
					scale *= 1 + (mWant.height / (mNow.height || 1) - 1) * settle;
					text.style.transform = `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`;

					const oNow = oWrap.getBoundingClientRect();
					const oWant = oSlot.getBoundingClientRect();
					lastLine.style.transform = `translate3d(${natX + ((oWant.left - oNow.left) / scale) * settle}px, ${natY + ((oWant.top - oNow.top) / scale) * settle}px, 0)`;

					const slashNow = slash.getBoundingClientRect();
					const slashWant = slashSlot.getBoundingClientRect();
					slash.style.transform = `translate3d(${((slashWant.left - slashNow.left) / scale) * settle}px, ${((slashWant.top - slashNow.top) / scale) * settle}px, 0)`;
				}
			}

			const docked = t > 0.992;
			layer.classList.toggle("is-docked", docked);
			layer.classList.toggle("is-moving", t > 0 && !docked);
			if (docked !== dockedRef.current) {
				dockedRef.current = docked;
				document.body.classList.toggle("name-docked", docked);
			}
		};

		const render = () => {
			const progress = clamp(window.scrollY / dockDistance());
			applyLetters(progress, reduced);
			applyPose(progress);
		};

		const schedule = () => {
			if (frame) return;
			frame = window.requestAnimationFrame(() => {
				frame = 0;
				render();
			});
		};

		const onResize = () => {
			dockMeasured = false;
			measureStart();
			schedule();
		};

		measureStart();
		render();
		window.addEventListener("scroll", schedule, { passive: true });
		window.addEventListener("resize", onResize);

		return () => {
			window.cancelAnimationFrame(frame);
			window.removeEventListener("scroll", schedule);
			window.removeEventListener("resize", onResize);
			document.body.classList.remove("name-docked");
			dockedRef.current = false;
			stopIdleGlitch();
			stopKeepScrollGlitch();
		};
	}, [location.pathname, target, keepO, mark, live]);

	if (location.pathname !== "/") {
		return null;
	}

	let charIndex = 0;

	return (
		<>
			<div className="name-intro-space" aria-hidden="true" />
			<div className="name-intro-layer" ref={layerRef}>
				<Link
					to="/"
					className="name-intro-text"
					ref={textRef}
					aria-label={`${text.site.displayName[0]} ${text.site.displayName[1]}`}
				>
					<span className="name-line">
						{first.split("").map(() => {
							const index = charIndex;
							charIndex += 1;
							return (
								<span
									key={`f-${index}`}
									className={`name-char${index === 0 ? " is-keep" : ""}`}
									ref={(node) => {
										charWraps.current[index] = node;
									}}
								>
									<span
										className="name-char-ghost name-char-ghost-a"
										aria-hidden="true"
									>
										{target[index]}
									</span>
									<span
										className="name-char-ghost name-char-ghost-b"
										aria-hidden="true"
									>
										{target[index]}
									</span>
									<span
										className="name-char-live"
										ref={(node) => {
											charsRef.current[index] = node;
										}}
									>
										{target[index]}
									</span>
								</span>
							);
						})}
						<span
							className="name-intro-slash"
							ref={slashRef}
							aria-hidden="true"
						>
							{mark[1]}
						</span>
					</span>
					<span className="name-line" ref={lastLineRef}>
						{last.split("").map(() => {
							const index = charIndex;
							charIndex += 1;
							return (
								<span
									key={`l-${index}`}
									className={`name-char${index === keepO ? " is-keep" : ""}`}
									ref={(node) => {
										charWraps.current[index] = node;
									}}
								>
									<span
										className="name-char-ghost name-char-ghost-a"
										aria-hidden="true"
									>
										{target[index]}
									</span>
									<span
										className="name-char-ghost name-char-ghost-b"
										aria-hidden="true"
									>
										{target[index]}
									</span>
									<span
										className="name-char-live"
										ref={(node) => {
											charsRef.current[index] = node;
										}}
									>
										{target[index]}
									</span>
								</span>
							);
						})}
					</span>
				</Link>
			</div>
		</>
	);
}
